// Deep-link "passage" model: a link points at a *sub-passage* of some linkable
// content (a recommender's note, a proposal field, …) — the exact text the user
// selected. A link has two parts:
//   • a LOCATOR (query params) that navigates the surface to the content, and
//   • an ANCHOR (the URL hash) that pins the passage within it:
//     `#passage=<text>&o=<offset>&s=<scopeId>`.
// The locator is surface-specific (injected by each surface); the anchor is
// surface-agnostic. `scopeId` names the DOM container (`data-passage-scope`) the
// landing search is restricted to, so it works the same on any surface.
//
// The passage text is content-bearing (it *is* the anchor). It is truncated to a
// sane length so links stay reasonable; landing does a substring match, so a
// truncated prefix still resolves as long as the content begins with that prefix.
//
// `normalizePassage`/`parse*` are pure (unit-testable); `buildPassageUrl` reads
// the current location + the app's public base URL to assemble a shareable link.

import { getClientAbsoluteBaseUrl } from "@client/trpc";

export const PASSAGE_HASH_PREFIX = "passage=";

// Anchor offset: the start index of the selected passage within the content's
// normalized text. Disambiguates which occurrence of a repeated passage the link
// points at; landing treats it as a hint (nearest-match), so it degrades safely.
export const PASSAGE_OFFSET_PREFIX = "o=";

// Anchor scope: identifies the DOM container (`data-passage-scope`) the landing
// search is restricted to. Surface-agnostic — every surface stamps its linkable
// container with this id.
export const PASSAGE_SCOPE_PREFIX = "s=";

// Cap the encoded passage so URLs don't balloon on long selections. Landing
// matches by substring, so a truncated prefix still lands on the passage.
export const MAX_PASSAGE_LENGTH = 300;

/**
 * Where a passage link points, minus the passage text itself:
 * - `params`: query params to (re)set so the surface navigates to the content
 *   (e.g. `{ tab: "all-notes", evaluator: memberId }` for a recommender note, or
 *   `{ tab: "proposal" }` for a proposal field). Unrelated existing params are
 *   preserved; only these keys are overwritten.
 * - `scopeId`: the id of the content's DOM container (its `data-passage-scope`),
 *   which scopes the landing search so the highlight lands in the right element.
 */
export type PassageLocator = {
  params: Record<string, string>;
  scopeId: string;
  /**
   * Absolute pathname the link should navigate to. Omit for surfaces whose
   * hosting page is the only place they render (links then keep the copier's
   * current pathname). Set it when the same content mounts on more than one
   * route (e.g. the eval modal renders on both funder tabs) so every minted
   * link points at the one canonical page that mounts landing for the scope.
   */
  path?: string;
};

// Scope-id convention for proposal *fields*: `field:<fieldId>`. Deliberately
// independent of *where* the field is rendered (main body vs Pinned Items): the
// same field id is stamped on every copy, so a link resolves for any viewer —
// the pinned copy is member-scoped, so a recipient who hasn't pinned the field
// would never match a pin-specific scope. Both copies show identical content, so
// landing on whichever is first in the DOM is correct.
const FIELD_SCOPE_PREFIX = "field:";

export function buildFieldScopeId(fieldId: string): string {
  return `${FIELD_SCOPE_PREFIX}${fieldId}`;
}

/** The field id embedded in a `field:…` scope, or null if `scope` isn't one. */
export function fieldIdFromScope(scope: string | null): string | null {
  if (scope == null || !scope.startsWith(FIELD_SCOPE_PREFIX)) return null;
  const fieldId = scope.slice(FIELD_SCOPE_PREFIX.length);
  return fieldId || null;
}

/**
 * Normalize a raw selection into the passage anchor we embed/look up: collapse
 * internal whitespace runs to single spaces, trim, and truncate to
 * `MAX_PASSAGE_LENGTH`. Returns null for an empty/whitespace-only selection.
 */
export function normalizePassage(raw: string): string | null {
  const collapsed = raw.replace(/\s+/g, " ").trim();
  if (!collapsed) return null;
  return collapsed.slice(0, MAX_PASSAGE_LENGTH);
}

/**
 * Build a deep link to a sub-passage of some linkable content, using the surface's
 * `locator` for navigation and appending the `#passage=…&o=…&s=…` anchor. Returns
 * null if the selection is empty.
 *
 * `offset` (optional) is the passage's start index within the content's normalized
 * text — it disambiguates which occurrence of a repeated passage the link points
 * at. Omitted from the URL when absent or 0 (the first occurrence needs no hint).
 *
 * Uses the app's public base URL (not `window.location.origin`) so links built
 * inside the bundled iPad app — where the WebView origin is `capacitor://localhost`
 * — are still openable in a browser or on another device. On web/dev-server this
 * resolves to the current origin.
 */
export function buildPassageUrl(
  locator: PassageLocator,
  selectedText: string,
  offset?: number | null,
): string | null {
  const passage = normalizePassage(selectedText);
  if (passage == null) return null;

  const pathname = locator.path ?? window.location.pathname;
  const origin = getClientAbsoluteBaseUrl();
  // A link's meaning is fully determined by its locator: carry ONLY the
  // locator's params. Copying the copier's current query string leaked stateful
  // nav params (e.g. a leftover `evaluator=` from a previously-followed note
  // link) into unrelated links, retargeting the recipient's view.
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(locator.params)) {
    params.set(key, value);
  }

  let hash = `${PASSAGE_HASH_PREFIX}${encodeURIComponent(passage)}`;
  if (offset != null && Number.isFinite(offset) && offset > 0) {
    hash += `&${PASSAGE_OFFSET_PREFIX}${Math.floor(offset)}`;
  }
  hash += `&${PASSAGE_SCOPE_PREFIX}${encodeURIComponent(locator.scopeId)}`;

  // Serialized string, not params.size: `.size` is undefined on Safari/WebView
  // < 17, where it would silently drop the query from every copied link.
  const queryString = params.toString();
  const query = queryString ? `?${queryString}` : "";
  return `${origin}${pathname}${query}#${hash}`;
}

/**
 * Build an OBJECT deep link: it targets a whole scoped unit (a summary card,
 * a modal) rather than a text passage inside it. The hash carries only the
 * scope (`#s=<scopeId>`, no `passage=`); landing scrolls the scope's element
 * into view and flashes it instead of quote-matching. Use for units whose
 * text shouldn't be selection-minted (pyramid summaries, where selection
 * fights click-to-expand) or that have no prose at all.
 */
export function buildObjectUrl(locator: PassageLocator): string {
  const pathname = locator.path ?? window.location.pathname;
  const origin = getClientAbsoluteBaseUrl();
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(locator.params)) {
    params.set(key, value);
  }
  const hash = buildObjectHash(locator.scopeId);
  const queryString = params.toString();
  const query = queryString ? `?${queryString}` : "";
  return `${origin}${pathname}${query}${hash}`;
}

/** Hash for a whole-unit landing, reusable by in-app router destinations. */
export function buildObjectHash(scopeId: string): string {
  return `#${PASSAGE_SCOPE_PREFIX}${encodeURIComponent(scopeId)}`;
}

/**
 * Locator for a proposal-field scope. Owns the load-bearing invariant that
 * field links carry NO query params: the proposal tab is the default, and the
 * page canonicalizes away an explicit `tab=proposal` via setSearchParams —
 * which would drop the `#passage` hash and break landing. Every field surface
 * must build its locator through this helper rather than hand-writing one.
 */
export function fieldLocator(fieldId: string): PassageLocator {
  return { params: {}, scopeId: buildFieldScopeId(fieldId) };
}

/**
 * The current location's hash, raw (percent-encoded). Always use this instead
 * of `window.location.hash`: Firefox's `location.hash` getter returns the
 * fragment percent-DECODED (a long-standing quirk kept for web-compat), which
 * corrupts passages containing `&` (splits the hash early) or `%` (malformed
 * escape → decode throws). The URL API is spec-compliant in all browsers.
 */
export function currentLocationHash(): string {
  return new URL(window.location.href).hash;
}

// Split a deep-link hash into its `&`-separated segments, or null if it isn't
// ours. Two accepted shapes, distinguished by the LEADING segment: a passage
// link (`passage=…` first, then optional `o=…`/`s=…`) or an OBJECT link
// (`s=…` alone — a whole-unit link with no passage; see buildObjectUrl).
// Anything else (plain page anchors like `#section`) is not a deep link.
function passageHashSegments(hash: string): string[] | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (
    !raw.startsWith(PASSAGE_HASH_PREFIX) &&
    !raw.startsWith(PASSAGE_SCOPE_PREFIX)
  ) {
    return null;
  }
  return raw.split("&");
}

/**
 * Parse the passage text out of a location hash. Returns the decoded passage, or
 * null if the hash is not a `#passage=` hash (or is empty/malformed). Accepts the
 * hash with or without a leading `#`, and ignores any trailing `&o=`/`&s=` parts.
 */
export function parsePassage(hash: string): string | null {
  const segments = passageHashSegments(hash);
  if (!segments) return null;
  return decodeSegment(segments[0]!, PASSAGE_HASH_PREFIX);
}

/**
 * Decode the value of one `key=value` hash segment. Returns null when the
 * segment doesn't match the prefix, is empty, or is malformed percent-encoding
 * (treated as no match rather than throwing).
 */
function decodeSegment(segment: string, prefix: string): string | null {
  if (!segment.startsWith(prefix)) return null;
  const encoded = segment.slice(prefix.length);
  if (!encoded) return null;
  try {
    const decoded = decodeURIComponent(encoded);
    return decoded || null;
  } catch {
    return null;
  }
}

/**
 * Parse the optional anchor offset (`&o=<int>`) out of a passage hash. Returns a
 * non-negative integer, or null if absent/malformed (landing then uses the first
 * match). Never throws.
 */
export function parsePassageOffset(hash: string): number | null {
  const segments = passageHashSegments(hash);
  if (!segments) return null;
  const segment = segments.find((s) => s.startsWith(PASSAGE_OFFSET_PREFIX));
  if (!segment) return null;
  const n = Number.parseInt(segment.slice(PASSAGE_OFFSET_PREFIX.length), 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Parse the anchor scope (`&s=<id>`) out of a passage hash — the id of the DOM
 * container landing should search within. Returns null if absent/malformed;
 * callers fall back to a surface-specific source (e.g. the `evaluator=` param of
 * an older recommender-note link that predates the `s=` anchor). Never throws.
 */
export function parsePassageScope(hash: string): string | null {
  const segments = passageHashSegments(hash);
  if (!segments) return null;
  const segment = segments.find((s) => s.startsWith(PASSAGE_SCOPE_PREFIX));
  if (!segment) return null;
  return decodeSegment(segment, PASSAGE_SCOPE_PREFIX);
}
